// src/components/dashboard/DashboardSummary.jsx
import React, { useState, useEffect, useCallback } from "react";
import "@/App.css";
import { Label } from "@/components/ui/label";
import MetricCardGroup from "../ui/MetricCardGroup";
import PostAnalysisXAxisSelector from "../selection/XAxisMetricsSelection";
import ChartMetricsSelection from "../selection/ChartMetricsSelection";
import PostAnalysisLineChart from "../charts/SummaryAreaChart";
import { PAALL_COLUMNS } from "../tables/table_columns";
import FilterBar from "../ui/FilterBar";
import { useGetSummaryData } from "@/hooks/post-analysis/useGetSummaryData";
import { formatValue } from "@/lib/post_analysis_utils/formatValue";
import SummarySkeleton from "../ui/SummarySkeleton";
import { exportToCSV } from "@/lib/post_analysis_utils/exportToCSV";
import FilterPills from "../ui/FilterPills";
import TableSkeleton from "../ui/TableSkeleton";
import UnifiedTable from "../tables/UnifiedTable";
import { fetchFilterOptions } from "@/services/api";
import { usePostAnalysisFilters } from "@/hooks/post-analysis/usePostAnalysisFilters";
import PostAnalysisHeaderWithFilters from "../ui/PostAnalysisHeaderWithFilters";

export default function Summary({
  filters = {
    brand: "",
    region: "",
    campaign: "",
    start_date: "",
    end_date: "",
    poc_id: [],
    key_account: ""
  },
  setFilters,
  campaignOptions = [],
}) {
  const [showFilters, setShowFilters] = useState(false);
  const [chartXAxis, setChartXAxis] = useState({
    metric: "Date",
    value: "date",
  });

  // console.log("the new x axis metrics are ",chartXAxis)
  const [chartYAxis, setChartYAxis] = useState({
    metric: "Total Impressions",
    value: "impressions",
  });

  // Use the hook for all summary data
  const {
    summaryMetrics,
    summaryChart,
    summaryTable,
    metricOptions,
    filterOptions: hookFilterOptions,
    isLoading,
    isTableLoading,
    error,
    getSummaryChart,
    getSummaryTable,
    getSummaryMetrics,
    getAllSummaryTable,
    tablePage,
    setTablePage,
    tablePageSize,
    setTablePageSize,
    tableTotalPages
  } = useGetSummaryData(
    filters.region,
    filters.brand,
    filters.campaign,
    filters.start_date,
    filters.end_date,
    filters.poc_id,
    filters.poc_name,
    filters.key_account,
    "summary"
  );

  // Shared local filter options + change handler, seeded from hookFilterOptions
  const { localFilterOptions, setLocalFilterOptions, handleLocalFiltersChange } =
    usePostAnalysisFilters(hookFilterOptions);

  // (Summary still uses fetchFilterOptions directly in this file for the hook; the
  // hook handles local filter-based updates to avoid duplication.)

  // Prepare metric cards data
  const socialMediaMetrics = [
    {
      key: "num_campaigns",
      label: "Total Campaigns",
      value: summaryMetrics?.num_campaigns,
      type: "pocs"
    },
    {
      key: "total_impressions",
      label: "Total Impressions",
      value: summaryMetrics?.total_impressions,
      type: "number"
    },
    { 
      key: "total_reach", 
      label: "Total Reach", 
      value: summaryMetrics?.total_reach,
      type: "number"
    },
    {
      key: "total_post_engagements",
      label: "Total Engagements",
      value: summaryMetrics?.total_post_engagements,
      type: "number"
    },
    {
      key: "average_er",
      label: "Average Engagement Rate",
      value: summaryMetrics?.average_er,
      type: "percent"
    },
  ].map((metric) => ({
    title: metric.label,
    value: formatValue(metric.value, { type: metric.type })
  }));

  const budgetMetrics = [
    {
      key: "total_budget",
      label: "Total Spend",
      value: summaryMetrics?.total_budget,
      type: "currency"
    },
    {
      key: "average_cpm",
      label: "Avg. CPM",
      value: summaryMetrics?.average_cpm,
      type: "currency"
    },
    {
      key: "total_volume_uplift",
      label: "Volume Uplift",
      value: summaryMetrics?.total_volume_uplift,
      type: "hl"
    },
    {
      key: "total_revenue",
      label: "Revenue",
      value: summaryMetrics?.total_revenue,
      type: "currency"
    },
  ].map((metric) => ({
    title: metric.label,
    value: formatValue(metric.value, { type: metric.type })
  }));

  // Helper to get axis/metric values from API response
  const getAxisValue = (val) => {
    if (!val) return "";
    const validYAxisMetrics = metricOptions?.y_axis || [];
    const validXAxisMetrics = metricOptions?.x_axis || [];
    const yAxisMetric = validYAxisMetrics.find((opt) => opt.label === val);
    const xAxisMetric = validXAxisMetrics.find((opt) => opt.label === val);
    return yAxisMetric?.value || xAxisMetric?.value || val;
  };

  // Update initial chart axes when metricOptions are loaded
  useEffect(() => {
    if (
      metricOptions?.x_axis?.length > 0 &&
      metricOptions?.y_axis?.length > 0
    ) {
      const defaultXAxis = metricOptions.x_axis.find((opt) => opt.value === "date") || metricOptions.x_axis[0];
      const defaultYAxis = metricOptions.y_axis.find((opt) => opt.value === "impressions") || metricOptions.y_axis[0];
      setChartXAxis({ 
        metric: defaultXAxis.label, 
        value: defaultXAxis.value,
      });
      setChartYAxis({ 
        metric: defaultYAxis.label,
        value: defaultYAxis.value,
        type: defaultYAxis.type 
      });
    }
  }, [metricOptions]);

  // Fetch chart data when axes or filters change
  useEffect(() => {
    if (chartXAxis && chartYAxis) {
      getSummaryChart(
        chartXAxis.value,
        chartYAxis.value
      );
    }
  }, [chartXAxis, chartYAxis, filters, getSummaryChart]);

  // Fetch table data when filters change
  useEffect(() => {
    getSummaryTable();
  }, [filters, getSummaryTable]);

  // Reset table page to 1 when filters change
  useEffect(() => {
    setTablePage(1);
  }, [filters]);

  // Prepare chart data
  const chartData = React.useMemo(() => {
    if (!summaryChart?.x_axis?.data || !summaryChart?.y_axis?.data) {
      return [];
    }

    // Get the metric type from the backend response
    const metricType = summaryChart.y_axis.type || 'number';
    
    const transformedData = summaryChart.x_axis.data.map((label, i) => ({
      label,
      value: summaryChart.y_axis.data[i],
      type: metricType
    }));
    return transformedData;
  }, [summaryChart]);

  // Prepare table data
  const tableData = React.useMemo(() => {
    if (!summaryTable?.data) {
      return [];
    }
    return summaryTable.data;
  }, [summaryTable]);

  // Pagination controls for All Metrics table
  const renderTablePagination = () => (
    <div className="flex items-center justify-between mt-2">
      <div>
        Page {tablePage} of {tableTotalPages}
      </div>
      <div className="flex gap-2">
        <button
          className="px-2 py-1 rounded border text-sm disabled:opacity-50"
          onClick={() => setTablePage((p) => Math.max(1, p - 1))}
          disabled={tablePage === 1}
        >
          Previous
        </button>
        <button
          className="px-2 py-1 rounded border text-sm disabled:opacity-50"
          onClick={() => setTablePage((p) => Math.min(tableTotalPages, p + 1))}
          disabled={tablePage === tableTotalPages}
        >
          Next
        </button>
      </div>
    </div>
  );

  // Export all data to CSV
  const handleExportAllCSV = async () => {
    try {
      const allData = await getAllSummaryTable(); // fetch all rows without triggering table skeleton
      const rows = allData.data || [];
      exportToCSV(rows, "all_metrics.csv");
    } catch (err) {
      alert("Failed to export all data. Please try again.");
    }
  };

  // Filter options are fetched by the hook, no need for duplicate fetching

  if (isLoading) return <SummarySkeleton />;
  if (error)
    return (
      <div className="text-center text-red-500 py-4">
        {error.message || "Error loading data"}
      </div>
    );

  return (
    <div className="p-4  pt-2 space-y-8">
      <PostAnalysisHeaderWithFilters
        title="Post Campaign Analysis"
        subtitle="Displaying data as per the selected filters"
        filters={filters}
        setFilters={setFilters}
        baseFilterOptions={hookFilterOptions}
        localFilterOptions={localFilterOptions}
        handleLocalFiltersChange={handleLocalFiltersChange}
      />

      {/* Main Content */}
      <div className="space-y-6">
        {/* Social Media Metrics */}
        <div className="space-y-2">
          <Label className="text-lg text-foreground">
            Social Media Metrics
          </Label>
          {summaryMetrics ? (
            <MetricCardGroup metrics={socialMediaMetrics} columns={5} />
          ) : (
            <div className="text-muted-foreground text-center py-4">
              No metrics available.
            </div>
          )}
        </div>

        {/* Chart and Budget Metrics */}
        <div className="flex gap-6">
          {/* Chart Section */}
          <div className="flex-1">
            <div className="flex items-center justify-left gap-8 mb-4">
              <div className="flex items-center gap-4">
                <span className="text-md font-medium text-foreground min-w-[50px]">
                  X-Axis:
                </span>
                <PostAnalysisXAxisSelector
                  initialValue={chartXAxis}
                  onSetMetric={setChartXAxis}
                  metricOptions={metricOptions?.x_axis || []}
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-md font-medium text-foreground min-w-[50px]">
                  Y-Axis:
                </span>
                <ChartMetricsSelection
                  value={chartYAxis.value}
                  onChange={(value) => {
                    const selected = (metricOptions?.y_axis || []).find(opt => opt.value === value);
                    setChartYAxis({
                      metric: selected?.label || value,
                      value,
                      label: selected?.label || value,
                      type: selected?.type || 'number'
                    });
                  }}
                  options={metricOptions?.y_axis || []}
                />
              </div>
            </div>
            <div className="h-full w-full">
              {chartData.length > 0 ? (
                <PostAnalysisLineChart
                  data={chartData}
                  XAxisMetrics={chartXAxis}
                  YAxisMetrics={chartYAxis}
                />
              ) : (
                <div className="text-muted-foreground text-center py-4">
                  No chart data available.
                </div>
              )}
            </div>
          </div>

          {/* Budget Metrics */}
          <div className="w-[260px] space-y-2 pt-12">
            <Label className="text-lg text-foreground">Budget Metrics</Label>
            <MetricCardGroup metrics={budgetMetrics} columns={1} />
          </div>
        </div>
      </div>

      {/* All Metrics Table */}
      <UnifiedTable
        columns={PAALL_COLUMNS}
        data={tableData}
        isLoading={isTableLoading}
        page={tablePage}
        setPage={setTablePage}
        pageSize={tablePageSize}
        setPageSize={setTablePageSize}
        totalPages={tableTotalPages}
        title="All Metrics"
        onExportCSV={handleExportAllCSV}
      />
    </div>
  );
}
